import React, { useState } from 'react';
import { SparkIcon, Tabs, TagPill, Text, TooltipTrigger } from '@spark';
import { TaskType, isGenerativeTask } from '@/shared/domain/taskTypes';
import { NONE_SELECTED } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import AnalysisPropertyRow from '@/features/datasets/components/DataCollection/dataOverview/sections/AnalysisPropertyRow';
import '@/features/datasets/components/DataCollection/dataOverview/sections/LabelsSection.css';

// Same heuristic as the develop branch's legacy `MetricsSection`: once a
// single output bucket (entities or relations) crosses 10 distinct values
// the dataset is hard to learn from, so we surface a small warning on the
// owning tab.
const TOO_MANY_OUTPUTS_THRESHOLD = 10;

const TabLabelWithWarning = ({ text, count, warningTooltip }) => (
    <span className="fs-unmask data-overview-labels-tab-label">
        {text}
        {count > TOO_MANY_OUTPUTS_THRESHOLD ? (
            <TooltipTrigger text={warningTooltip} placement="top">
                <span className="data-overview-labels-warning-icon">
                    <SparkIcon slug="circle-alert-triangle" size="small" />
                </span>
            </TooltipTrigger>
        ) : null}
    </span>
);

const LabelChip = ({ label, multiple = false }) => {
    const labelContent = `${label.name} ${label.percentage}`;
    const isWarning = Boolean(label.warning && multiple);
    const tooltipTitle = isWarning
        ? `Not enough examples (${label.count})`
        : `${label.count} examples`;

    return (
        <TooltipTrigger text={tooltipTitle} placement="top">
            <span className="data-overview-label-chip">
                {isWarning ? (
                    <TagPill variant="warning" label={labelContent} />
                ) : (
                    <TagPill label={labelContent} />
                )}
            </span>
        </TooltipTrigger>
    );
};

const LabelsGrid = ({ labels }) => (
    <div className="fs-mask labels-grid-container data-overview-labels-chips">
        {labels.map((label, index) => (
            <LabelChip key={`${label.name}-${index}`} label={label} multiple={labels?.length > 10} />
        ))}
    </div>
);

/*
 * Wrapped pill row (Figma 3001:68875) — pill + warning triangle (tooltip)
 * for labels that don't have enough examples.
 */
const LabelsList = ({ labels }) => (
    <div className="fs-mask data-overview-labels-chips">
        {labels.map((label, index) => (
            <span key={`${label.name}-${index}`} className="data-overview-labels-chip-item">
                <LabelChip label={label} />
                {label.warning ? (
                    <TooltipTrigger
                        text="This label doesn't have enough examples"
                        placement="top"
                    >
                        <span className="data-overview-labels-warning-icon">
                            <SparkIcon slug="circle-alert-triangle" size="small" />
                        </span>
                    </TooltipTrigger>
                ) : null}
            </span>
        ))}
    </div>
);

const buildLabelsList = (labels) => {
    if (!labels || Object.keys(labels).length === 0) return [];

    const totalLabelCount = Object.values(labels).reduce(
        (sum, labelData) => sum + (labelData.total || 0),
        0
    );

    const labelsList = Object.entries(labels).map(([labelName, labelData]) => {
        const count = labelData.total || 0;
        const percentage =
            totalLabelCount > 0 ? parseFloat(((count / totalLabelCount) * 100).toFixed(2)) : 0;
        const warning = count < 15;
        return {
            name: labelName ? labelName : NONE_SELECTED,
            percentage: `${percentage}%`,
            warning,
            count,
            isNoneSelected: !labelName,
        };
    });

    return labelsList.sort((a, b) => {
        if (a.isNoneSelected && !b.isNoneSelected) return 1;
        if (!a.isNoneSelected && b.isNoneSelected) return -1;
        if (a.isNoneSelected && b.isNoneSelected) return 0;
        return a.name.localeCompare(b.name);
    });
};

const LabelsChips = ({ labels }) => {
    const sortedLabelsList = buildLabelsList(labels);
    if (sortedLabelsList.length === 0) return null;
    const hasManyLabels = sortedLabelsList.length > 10;
    return hasManyLabels ? (
        <LabelsGrid labels={sortedLabelsList} />
    ) : (
        <LabelsList labels={sortedLabelsList} />
    );
};

/**
 * Renders the "Labels Detected" / "Entities Detected" / "Relations Detected"
 * panel. Pure UI: takes a counts dict and a denominator and renders chips with
 * percentages, plus a warning when a label has fewer than 15 examples.
 */
const LabelsPanel = ({
    labels,
    title = 'Labels Detected',
    showHeader = true,
    countValue,
    countCaption,
}) => {
    const sortedLabelsList = buildLabelsList(labels);
    if (sortedLabelsList.length === 0) return null;

    const chips = <LabelsChips labels={labels} />;
    const numericCount =
        countValue != null ? Number(countValue) : sortedLabelsList.length;
    const warningTooltip =
        title?.includes?.('Relations')
            ? 'Too many relations'
            : title?.includes?.('Entities')
                ? 'Too many entities'
                : 'Too many labels';

    const countNode = (
        <span className="data-overview-labels-count">
            {numericCount}
            {numericCount > TOO_MANY_OUTPUTS_THRESHOLD ? (
                <TooltipTrigger text={warningTooltip} placement="top">
                    <span className="data-overview-labels-warning-icon">
                        <SparkIcon slug="circle-alert-triangle" size="small" />
                    </span>
                </TooltipTrigger>
            ) : null}
        </span>
    );

    if (!showHeader) {
        return (
            <div className="data-overview-labels-panel">
                {countCaption ? (
                    <Text
                        as="div"
                        variant="detail-regular"
                        color="tertiary"
                        className="data-overview-labels-caption"
                    >
                        {countCaption}
                    </Text>
                ) : null}
                <div className="data-overview-labels-chips-wrap">{chips}</div>
            </div>
        );
    }

    return (
        <div className="data-overview-labels-panel">
            <div className="data-overview-labels-header">
                <AnalysisPropertyRow
                    icon="database"
                    label={<span className="fs-unmask">{title}</span>}
                    value={countNode}
                />
                {countCaption ? (
                    <Text
                        as="div"
                        variant="detail-regular"
                        color="tertiary"
                        className="data-overview-labels-caption"
                    >
                        {countCaption}
                    </Text>
                ) : null}
            </div>
            <div className="data-overview-labels-chips-wrap">{chips}</div>
        </div>
    );
};

const NerWithRelationsLabels = ({ data, setActiveTab, showHeader }) => {
    const [activeId, setActiveId] = useState('entities');
    const entitiesCount = data.entities ? Object.keys(data.entities).length : 0;
    const relationsCount = data.relations ? Object.keys(data.relations).length : 0;

    const handleChange = (id) => {
        setActiveId(id);
        setActiveTab?.(id);
    };

    const activeLabels = activeId === 'relations' ? data.relations : data.entities;
    const activeTitle = activeId === 'relations' ? 'Relations Detected' : 'Entities Detected';
    const activeCaption = activeId === 'relations' ? '# of Relations' : '# of Entities';
    const activeCount = activeId === 'relations' ? relationsCount : entitiesCount;

    return (
        <div className="data-overview-labels-section">
            <Tabs
                size="medium"
                activeId={activeId}
                onChange={handleChange}
                items={[
                    {
                        id: 'entities',
                        label: (
                            <TabLabelWithWarning
                                text="Entities"
                                count={entitiesCount}
                                warningTooltip="Too many entities"
                            />
                        ),
                    },
                    {
                        id: 'relations',
                        label: (
                            <TabLabelWithWarning
                                text="Relations"
                                count={relationsCount}
                                warningTooltip="Too many relations"
                            />
                        ),
                    },
                ]}
            />
            <LabelsPanel
                labels={activeLabels}
                title={activeTitle}
                showHeader={showHeader}
                countValue={activeCount}
                countCaption={activeCaption}
            />
        </div>
    );
};

/**
 * Task-type aware "Output Detected" panel. Picks labels / entities / relations
 * based on `selectedTaskType`, with a tab strip for NER + Relations.
 */
const LabelsSection = ({ data, selectedTaskType, setActiveTab, showHeader = true }) => {
    if (!data) return null;

    if (isGenerativeTask(selectedTaskType)) return null;

    if (selectedTaskType === TaskType.NER_WITH_RELATIONS) {
        return (
            <NerWithRelationsLabels
                data={data}
                setActiveTab={setActiveTab}
                showHeader={showHeader}
            />
        );
    }

    if (selectedTaskType === TaskType.classification) {
        return (
            <LabelsPanel
                labels={data.labels}
                title="Labels Detected"
                showHeader={showHeader}
                countValue={data.labels ? Object.keys(data.labels).length : 0}
                countCaption="# of Labels"
            />
        );
    }

    if (selectedTaskType === TaskType.NER) {
        return (
            <LabelsPanel
                labels={data.entities}
                title="Entities Detected"
                showHeader={showHeader}
                countValue={data.entities ? Object.keys(data.entities).length : 0}
                countCaption="# of Entities"
            />
        );
    }

    return null;
};

export default LabelsSection;
